import React from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import booksData from './booksData';
import './BookDetails.css';

const SITE_URL = 'https://jerryevanoff.com';

const SERIES_BY_CATEGORY = {
  murderMystery: {
    label: 'Sam Norris Murder Mystery Series',
    genre: ['Amateur Sleuth Mystery', 'Murder Mystery', 'Modern Whodunit', 'Soft-Boiled Mystery']
  },
  timeTravel: {
    label: 'Nightmares Through Time',
    genre: ['Time Travel Fiction', 'Science Fiction']
  }
};

const truncate = (text, maxLength) => {
  if (!text || text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength).replace(/\s+\S*$/, '')}…`;
};

const BookDetails = () => {
  const { bookIndex } = useParams();

  const book = booksData.flatMap((series) => series.books).find((book) => book.id === parseInt(bookIndex));

  if (!book) {
    return <div>Book not found</div>;
  }

  const series = SERIES_BY_CATEGORY[book.category] || { label: '', genre: [] };
  const pageUrl = `${SITE_URL}/bookdetails/${book.id}`;
  const pageTitle = series.label ? `${book.title} by Jerry Evanoff | ${series.label}` : `${book.title} by Jerry Evanoff`;
  const pageDescription = truncate(book.description, 160);
  const coverImage = book.cover ? `${SITE_URL}${book.cover}` : undefined;
  const bodyContent = book.longDescription || book.description;

  const bookSchema = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: book.title,
    author: {
      '@type': 'Person',
      name: 'Jerry Evanoff',
      url: SITE_URL
    },
    ...(series.label && {
      isPartOf: {
        '@type': 'BookSeries',
        name: series.label,
        url: `${SITE_URL}/books`
      }
    }),
    genre: series.genre,
    description: book.description,
    ...(coverImage && { image: coverImage }),
    url: pageUrl,
    ...(book.buyLink && {
      offers: [
        {
          '@type': 'Offer',
          url: book.buyLink,
          availability: 'https://schema.org/InStock'
        }
      ]
    })
  };

  return (
    <div className="book-details-container">
      <Helmet>
        <title>{pageTitle}</title>

        {pageDescription && <meta name="description" content={pageDescription} />}
        <meta name="author" content="Jerry Evanoff" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="book" />
        <meta property="og:title" content={pageTitle} />
        {pageDescription && <meta property="og:description" content={pageDescription} />}
        {coverImage && <meta property="og:image" content={coverImage} />}
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="Jerry Evanoff" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        {pageDescription && <meta name="twitter:description" content={pageDescription} />}

        <script type="application/ld+json">
          {JSON.stringify(bookSchema)}
        </script>
      </Helmet>

      <div className={`book-details ${book.currentStatus ? 'three-cols' : 'two-cols'}`}>
        <img src={book.cover} alt={book.title} className="book-cover" />
        <div className="book-description-container">
          <div
            className="book-long-description"
            dangerouslySetInnerHTML={{ __html: bodyContent }}
          />
          {book.showBuyButton && (
            <a href={book.buyLink} className="book-buy-button" target="_blank" rel="noopener noreferrer">
              {book.buyButtonText}
            </a>
          )}
        </div>
        {book.currentStatus && (
          <div className="book-status-container">
            <h3 className="status-heading">Current Book Status</h3>
            <div
              className="book-current-status"
              dangerouslySetInnerHTML={{ __html: book.currentStatus }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BookDetails;
